import React, { useEffect, useMemo, useState } from 'react';
import { Copy, UserPlus, Users } from 'lucide-react';

export default function TeamSettings() {
  const [users, setUsers] = useState([]);
  const [sites, setSites] = useState([]);
  const [newUser, setNewUser] = useState({ email: '', access_policy: 'all', site_ids: [] });
  const [message, setMessage] = useState('');
  const [lastInvite, setLastInvite] = useState(null);

  useEffect(() => { fetchUsers(); fetchSites(); }, []);

  async function fetchUsers() {
    const res = await fetch('/api/users');
    if (res.ok) {
      const data = await res.json();
      setUsers(data.users || []);
    }
  }

  async function fetchSites() {
    const res = await fetch('/api/sites');
    if (res.ok) {
      const data = await res.json();
      setSites(data.sites || []);
    }
  }

  const superadmins = useMemo(() => users.filter(u => u.role === 'superadmin'), [users]);
  const members = useMemo(() => users.filter(u => u.role !== 'superadmin'), [users]);

  function toggleSite(siteId) {
    const current = new Set(newUser.site_ids);
    if (current.has(siteId)) current.delete(siteId);
    else current.add(siteId);
    setNewUser({ ...newUser, site_ids: Array.from(current) });
  }

  async function handleAddUser(e) {
    e.preventDefault();
    setMessage('Creating member invite...');
    setLastInvite(null);
    const res = await fetch('/api/users', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(newUser),
    });
    const data = await res.json();
    if (!res.ok) {
      setMessage(data.error || 'Failed to add member');
      return;
    }
    setMessage('Member invite link generated. Copy and send it manually to the member.');
    setLastInvite(data);
    setNewUser({ email: '', access_policy: 'all', site_ids: [] });
    await fetchUsers();
  }

  async function copyInvite() {
    if (!lastInvite?.invite_url) return;
    await navigator.clipboard.writeText(lastInvite.invite_url);
    setMessage('Invite link copied.');
  }

  return <section className="card team-card">
    <h2><Users size={20} /> Team Settings</h2>
    <p className="muted">Only the superadmin can invite members. Members can access all sites or only selected sites, but cannot manage users.</p>
    {message && <div className="notice slim">{message}</div>}

    <form onSubmit={handleAddUser} className="form team-form">
      <label>Member email</label>
      <input type="email" value={newUser.email} onChange={e => setNewUser({ ...newUser, email: e.target.value })} placeholder="member@sweetmag.com.my" required />

      <label>Access policy</label>
      <select value={newUser.access_policy} onChange={e => setNewUser({ ...newUser, access_policy: e.target.value, site_ids: [] })}>
        <option value="all">All sites</option>
        <option value="selected">Selected sites only</option>
      </select>

      {newUser.access_policy === 'selected' && <div className="site-checkbox-panel">
        <label>Choose sites</label>
        {sites.length === 0 && <p className="muted">No sites available yet.</p>}
        {sites.map(site => <label key={site.id} className="check-row">
          <input type="checkbox" checked={newUser.site_ids.includes(site.id)} onChange={() => toggleSite(site.id)} />
          <span><strong>{site.name}</strong><small>{site.domain}</small></span>
        </label>)}
      </div>}

      <button type="submit"><UserPlus size={16} /> Invite Member</button>
    </form>

    {lastInvite?.invite_url && <div className="invite-output">
      <div>
        <strong>Invite Link</strong>
        <p>{lastInvite.email_note}</p>
        <code>{lastInvite.invite_url}</code>
      </div>
      <button type="button" onClick={copyInvite}><Copy size={16} /> Copy</button>
    </div>}

    <div className="team-grid">
      <div>
        <h3>Superadmin</h3>
        <div className="user-list">
          {superadmins.map(u => <UserRow key={u.id} user={u} />)}
        </div>
      </div>
      <div>
        <h3>Members</h3>
        <div className="user-list">
          {members.length === 0 && <p className="muted">No members invited yet.</p>}
          {members.map(u => <UserRow key={u.id} user={u} />)}
        </div>
      </div>
    </div>

  </section>;
}

function UserRow({ user }) {
  const statusClass = user.status === 'active' ? 'ok' : 'warn';
  return <div className="user-row">
    <div>
      <strong>{user.email || user.username}</strong>
      <small>{user.role} · <span className={`status-text ${statusClass}`}>{user.status || 'active'}</span> · {user.access_policy === 'selected' ? 'Selected sites' : 'All sites'}</small>
      {user.access_policy === 'selected' && <em>{(user.sites || []).map(s => s.name).join(', ') || 'No sites selected'}</em>}
    </div>
  </div>;
}
